import React, { useState, useEffect, useCallback, useRef } from 'react';
import { oilApi, fmt } from './oilApi';
import { OilIcon, KakoCoin } from './OilIcons';
import OilRankRules from './OilRankRules';
import { useOilLocale } from '../../i18n/OilLocale';
import { oilArt } from '../../i18n/oilArt';
import { sfx, sfxOpen, sfxTab } from './oilSounds';

const AV = ({ row, cls }) => (
  row?.avatar
    ? <img src={row.avatar} alt="" className={`${cls} rounded-full object-cover`} />
    : <div className={`${cls} flex items-center justify-center rounded-full bg-gradient-to-b from-zinc-800 to-black text-[1em] leading-none text-amber-500/80`}>🦊</div>
);

/* posições medidas em pixels sobre a arte do pódio (1100x696) */
const PW = 1100, PH = 696;
const box = (x, y, w, h) => ({
  left: `${(x / PW) * 100}%`, top: `${(y / PH) * 100}%`,
  width: `${(w / PW) * 100}%`, ...(h ? { height: `${(h / PH) * 100}%` } : {}),
});
const POD = [
  { circle: box(451, 155, 198), plate: box(365, 428, 370, 70), big: true },
  { circle: box(125, 251, 154), plate: box(100, 458, 232, 45) },
  { circle: box(823, 251, 152), plate: box(768, 458, 230, 45) },
];

function Podium({ rows, label, value }) {
  return (
    <div className="relative mx-auto w-full max-w-[430px]" style={{ aspectRatio: `${PW}/${PH}` }} data-testid="oil-podium">
      {/* avatares entram atrás da moldura: os ornamentos sobrepõem naturalmente */}
      {POD.map((pos, i) => rows[i] && (
        <div key={`av-${i}`} className="absolute z-0 overflow-hidden rounded-full"
          style={{ ...pos.circle, aspectRatio: '1/1', fontSize: pos.big ? 'clamp(20px,7vw,54px)' : 'clamp(16px,5.5vw,42px)' }}
          data-testid={`oil-podium-avatar-${i + 1}`}>
          <AV row={rows[i]} cls="h-full w-full" />
        </div>
      ))}
      <img src="/oil/podium_top3.webp" alt="" className="pointer-events-none absolute inset-0 z-10 h-full w-full object-contain drop-shadow-2xl" />
      {POD.map((pos, i) => rows[i] && (
        <div key={`plate-${i}`} className="absolute z-20 flex flex-col items-center justify-center overflow-hidden px-[2%] leading-none"
          style={pos.plate} data-testid={`oil-podium-${i + 1}`}>
          <p className="w-full truncate text-center font-black text-amber-100"
            style={{ fontSize: pos.big ? 'clamp(9px,2.9vw,13px)' : 'clamp(7px,2.1vw,10px)' }}>
            {label(rows[i])}
          </p>
          <p className="mt-[0.35em] flex w-full items-center justify-center gap-[0.25em] truncate font-black text-amber-400"
            style={{ fontSize: pos.big ? 'clamp(8px,2.4vw,11px)' : 'clamp(6px,1.85vw,9px)' }}>
            {value(rows[i])}
          </p>
        </div>
      ))}
    </div>
  );
}

function RankList({ rows, label, value, startAt, testid }) {
  return (
    <div className="relative mx-auto -mt-3 w-full max-w-[430px]" data-testid={`${testid}-frame`}
      style={{
        borderStyle: 'solid',
        borderWidth: '30px 34px 26px 34px',
        borderImage: "url('/oil/frame_wolves.webp') 15% 17% 13% 17% round",
      }}>
      <div className="max-h-[290px] overflow-y-auto bg-[#0b0a08] px-1 py-1" data-testid={testid}>
        {rows.length === 0 ? (
          <p className="py-5 text-center text-[11px] font-bold text-zinc-400">Ninguém mais no ranking ainda.</p>
        ) : rows.map((r, i) => {
          const rank = startAt + i;
          return (
            <div key={label(r) + rank} data-testid={`${testid}-row-${rank}`}
              className={`mb-1 flex items-center gap-2 rounded-lg border px-2 py-1.5 ${rank <= 10 ? 'border-amber-700/60 bg-gradient-to-r from-amber-950/70 to-black' : 'border-zinc-800 bg-zinc-900/50'}`}>
              <span className={`w-7 shrink-0 text-center text-[11px] font-black ${rank <= 10 ? 'text-amber-300' : 'text-zinc-400'}`}>{rank}º</span>
              <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-amber-900/60">
                <AV row={r} cls="h-full w-full" />
              </div>
              <p className="min-w-0 flex-1 truncate text-[11px] font-black text-amber-100">{label(r)}</p>
              {rank <= 10 && <span className="shrink-0 text-[10px]">🏆</span>}
              <span className="shrink-0 text-[10px] font-black text-amber-300">{value(r)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const JackpotPill = ({ value, testid, audible = true }) => {
  const [bump, setBump] = useState(null);   // { id, delta } → setinha verde subindo
  const anterior = useRef(null);

  useEffect(() => {
    if (value === null || value === undefined) return undefined;
    const atual = Number(value) || 0;
    const antes = anterior.current;
    anterior.current = atual;
    if (antes === null || atual <= antes) return undefined;
    const id = `${Date.now()}-${atual}`;
    setBump({ id, delta: atual - antes });
    if (audible) sfx.coin();
    const t = setTimeout(() => setBump((b) => (b?.id === id ? null : b)), 2400);
    return () => clearTimeout(t);
  }, [value, audible]);

  return (
    <div className="relative mx-auto mb-2 flex w-fit flex-col items-center rounded-2xl border border-amber-500/70 bg-black/70 px-5 pb-2 pt-1.5 backdrop-blur"
      style={{ animation: 'jackpot-neon 2.2s ease-in-out infinite' }} data-testid={`${testid}-pill`}>
      <img src="/oil/title_jackpot.webp" alt="JACKPOT" className="w-[160px] max-w-full drop-shadow-xl" />
      <div className="relative flex items-center gap-1">
        <p className={`text-base font-black transition-colors duration-300 ${bump ? 'text-emerald-300' : 'text-amber-200'}`}
          style={bump ? { animation: 'jackpot-pop 0.6s ease-out' } : undefined}
          data-testid={testid}><KakoCoin /> {fmt(value)}</p>
        {bump && (
          <span key={bump.id}
            className="pointer-events-none absolute left-full top-1/2 ml-1.5 flex -translate-y-1/2 items-center gap-0.5 whitespace-nowrap rounded-full border border-emerald-400/70 bg-emerald-500/25 px-1.5 py-[1px] text-[11px] font-black text-emerald-300 drop-shadow-[0_0_8px_rgba(16,185,129,0.85)]"
            style={{ animation: 'jackpot-float 2.4s ease-out forwards' }}
            data-testid={`${testid}-up-arrow`}>
            <span className="text-[13px] leading-none">▲</span>
            <span data-testid={`${testid}-delta`}>+{fmt(bump.delta)}</span>
          </span>
        )}
      </div>
      <p className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-500">Acumulado</p>
      <style>{`@keyframes jackpot-neon {
        0%, 100% { box-shadow: 0 0 5px rgba(255,190,60,0.35), inset 0 0 8px rgba(255,170,0,0.08); }
        50% { box-shadow: 0 0 16px rgba(255,205,90,0.85), 0 0 30px rgba(255,150,0,0.45), inset 0 0 12px rgba(255,180,0,0.18); }
      }
      @keyframes jackpot-float {
        0% { opacity: 0; transform: translate(0, 4px) scale(0.8); }
        15% { opacity: 1; transform: translate(0, -50%) scale(1.1); }
        80% { opacity: 1; transform: translate(0, -60%) scale(1); }
        100% { opacity: 0; transform: translate(0, -180%) scale(0.9); }
      }
      @keyframes jackpot-pop {
        0% { transform: scale(1); }
        40% { transform: scale(1.14); }
        100% { transform: scale(1); }
      }`}</style>
    </div>
  );
};

const RulesBtn = ({ onClick }) => (
  <div className="relative z-30 -mb-2 flex justify-end pr-2">
    <button onClick={onClick} data-testid="oil-rank-rules-btn"
      className="flex h-16 w-16 items-center justify-center transition-transform active:scale-90"
      style={{ animation: 'rules-neon 2s ease-in-out infinite' }}>
      <img src="/oil/ic_regras.webp" alt="Regras do ranking" className="h-full w-full object-contain" />
      <style>{`@keyframes rules-neon {
        0%, 100% { filter: drop-shadow(0 0 3px rgba(255,190,60,0.5)); }
        50% { filter: drop-shadow(0 0 9px rgba(255,215,110,0.95)) drop-shadow(0 0 18px rgba(255,150,0,0.6)); }
      }`}</style>
    </button>
  </div>
);

function OilRankings({ state, rankTick, active = true }) {
  const [data, setData] = useState(null);
  const { locale } = useOilLocale();
  const [sub, setSub] = useState('magnates');
  const [rulesOpen, setRulesOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await oilApi.get('/rankings'); setData(r.data); } catch {}
  }, []);
  // abrir a aba (ou receber evento do servidor) recarrega na hora
  useEffect(() => { if (active) load(); }, [load, rankTick, active]);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 10000);   // rede lenta/WS caído: posição nunca fica velha
    const onBack = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onBack);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [load, active]);

  const jackpot = data?.jackpot || state.season?.jackpot || {};
  const magnates = data?.magnates || [];
  const brokers = data?.shareholders || [];
  const coinsPerAsset = state.config?.shareholder_score?.coins_per_asset || 1000;
  const payout = state.config?.jackpot_payout_top10 || [];
  const payoutBrokers = state.config?.jackpot_payout_shareholders || [];
  const myRank = data?.magnates_me?.rank;
  const estPrize = Math.floor(
    (Number(jackpot.magnates || 0) *
      Number((payout.find((p) => p.rank === myRank) || {}).pct || 0)) / 100);

  const magLabel = (r) => r.nickname;
  const magValue = (r) => <><OilIcon k="fox" /> {fmt(r.fox_dollars)}</>;
  const brkLabel = (r) => r.name;
  const brkValue = (r) => `${fmt(r.assets)} ativos`;

  return (
    <div className="space-y-3 pb-4" data-testid="oil-rankings">
      {/* ===== SUB-ABAS ===== */}
      <div className="mx-auto flex max-w-[430px] items-center gap-2" data-testid="oil-rank-subtabs">
        {[{ id: 'magnates', img: 'magnatas' }, { id: 'brokers', img: 'corretores' }].map((t) => (
          <button key={t.id} onClick={() => { if (t.id !== sub) sfxTab(); setSub(t.id); }} data-testid={`oil-rank-subtab-${t.id}`}
            className="min-w-0 flex-1 transition-transform active:scale-95">
            <img src={oilArt(`tab_${t.img}_${sub === t.id ? 'on' : 'off'}`, locale)} alt={t.img}
              className={`w-full drop-shadow-lg transition-all ${sub === t.id ? '' : 'opacity-70'}`} />
          </button>
        ))}
      </div>

      <RulesBtn onClick={() => { sfxOpen(); setRulesOpen(true); }} />

      {rulesOpen && (
        <OilRankRules kind={sub} jackpot={jackpot} coinsPerAsset={coinsPerAsset}
          payout={sub === 'magnates' ? payout : payoutBrokers}
          onClose={() => setRulesOpen(false)} />
      )}

      <div className="relative" style={{ display: sub === 'magnates' ? 'block' : 'none' }}>
          <img src={oilArt('title_magnatas', locale)} alt="RANKING DOS MAGNATAS" className="mx-auto mb-1 w-full max-w-[320px] drop-shadow-xl" />
          <JackpotPill value={jackpot.magnates} testid="oil-jackpot-magnates"
            audible={sub === 'magnates'} />
          {magnates.length === 0 ? (
            <p className="rounded-xl bg-black/60 px-4 py-6 text-center text-xs font-bold text-zinc-300">Ainda sem magnatas. Venda barris para entrar no pódio! 👑</p>
          ) : (
            <>
              <Podium rows={magnates.slice(0, 3)} label={magLabel} value={magValue} />
              <RankList rows={magnates.slice(3)} label={magLabel} value={magValue} startAt={4}
                testid="magnates-list" />
            </>
          )}
          {data?.magnates_me && (
            <div className="mx-auto mt-2 max-w-[430px] rounded-xl border-2 border-amber-600/70 bg-gradient-to-r from-[#1a1204] to-black px-3 py-2.5" data-testid="magnates-my-position">
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-500">Sua posição</p>
              <div className="mt-1 flex items-center gap-2.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-600/60 bg-amber-500/15 text-sm font-black text-amber-300" data-testid="magnates-my-rank">
                  {data.magnates_me.rank ? `${data.magnates_me.rank}º` : '—'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-black text-amber-100"><OilIcon k="fox" /> {fmt(data.magnates_me.fox_dollars)} FOX Dólar</p>
                  {estPrize > 0 && (
                    <p className="text-[10px] font-black text-amber-300" data-testid="magnates-est-prize">
                      <KakoCoin /> Prêmio estimado: {fmt(estPrize)} moedas
                      <span className="font-bold text-zinc-500"> (se a Season fechasse agora)</span>
                    </p>
                  )}
                  {data.magnates_me.rank === 1
                    ? <p className="text-[10px] font-bold text-amber-400">Você é o maior Magnata da temporada! 👑</p>
                    : <p className="text-[10px] text-zinc-400">Faltam <b className="text-amber-300">{fmt(data.magnates_me.gap_next)}</b> FOX Dólar para subir de posição</p>}
                  {(!data.magnates_me.rank || data.magnates_me.rank > 10) && (
                    <p className="text-[10px] text-zinc-400">Faltam <b className="text-amber-300">{fmt(data.magnates_me.gap_top10)}</b> para entrar no Top 10 premiado 🏆</p>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>


      <div className="relative" style={{ display: sub === 'brokers' ? 'block' : 'none' }}>
          <img src={oilArt('title_corretores', locale)} alt="RANKING DOS CORRETORES" className="mx-auto mb-1 w-full max-w-[320px] drop-shadow-xl" />
          <JackpotPill value={jackpot.shareholders} testid="oil-jackpot-shareholders"
            audible={sub === 'brokers'} />
          {brokers.length === 0 ? (
            <p className="rounded-xl bg-black/60 px-4 py-6 text-center text-xs font-bold text-zinc-300">Ainda sem corretores pontuando. 💼</p>
          ) : (
            <>
              <Podium rows={brokers.slice(0, 3)} label={brkLabel} value={brkValue} />
              <RankList rows={brokers.slice(3)} label={brkLabel} value={brkValue} startAt={4}
                testid="brokers-list" />
            </>
          )}
      </div>

    </div>
  );
}

export default React.memo(OilRankings);
